/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import BaseSummarizerQuery from './BaseSummarizerQuery';

const __SUMMARY_MIN_PREFIX__ = '__min__';

/**
 * The OverallSummaryValueQuery is a single query that updates summaryCollector entries
 * with the single row result of the aggregated summary values
 * for all measures (ordinals) that are projected. (Because they're overall summaries, all layers can also be done at once for multi-layer)
 * It also is used to detect if datasets include negatives - mins are negative (to warn the user summaries may be > 100% of the data)
 * NOTE: it may return cached results for performance.
 * Case1: Initial execute (will query for all items)
 * Case2: User inserted a new measure(s) (will query for that measure)
 * Case3: Shaping changed...all measures will be queried for.
 **/
class OverallSummaryValueQuery extends BaseSummarizerQuery {

	/**
	 * @Constructor
	 * @param {Object} widgetApi - the owner widget (needed to access services)
	 * @param {Object} dashboardApi - the dashboardApi
	 * @param {Object} logger - The logger
	 */
	constructor(widgetApi, dashboardApi, logger) {
		super(widgetApi, dashboardApi, logger);

		//The overallSummariesCache maintains previous results since values rarely change.
		this.overallSummariesCache={};

		//The overallSummaryValue query should not be filtered (it is measuring all data)
		this.includeFilters = false;
	}

	//If the widget shaping changed, we need to refresh overall summaries.
	onWidgetShapingChanged() {
		this.overallSummariesCache={};
	}

	/**
	 * Add an item to the overallSummaryValue query definition UNLESS
	 * 1) it is not a fact
	 * 2) if has been requested before (in which case there's no need to query for it again)
	 * @param {object} queryDef - the queryDefinition that will be modified with this item if it applies
	 * @param {object[]} queryDef.dataItems - simple data item _querySpec
	 * @param {String[]} queryDef.projections - the list of projected items (ids same as dataItems list)
	 * @param summaryCollector - a summaryCollector (or selectionSummaryCollector) whose entries are to be augmented with the overallSummary values.
	*/
	addQueryItem(queryDef, mappedDataItem, summaryCollector) {
		if (mappedDataItem.getType()==='fact') {
			let itemUpdated=false;
			const itemSummaryCacheEntry = this.overallSummariesCache[mappedDataItem.getId()];
			if (itemSummaryCacheEntry && itemSummaryCacheEntry.aggregationType === mappedDataItem.getAggregation()) {
				let summaryEntry = this.updateSummaryEntry(summaryCollector, mappedDataItem.getId(), itemSummaryCacheEntry.value, 'summaryValueUse', 'summaryValue');
				if (summaryEntry && itemSummaryCacheEntry.hasNegativeValues) {
					summaryEntry.hasNegativeValues = itemSummaryCacheEntry.hasNegativeValues;
				}
				itemUpdated=true;
			}
			if (!itemUpdated) {
				const hasDataItem = (id) => {
					return queryDef.dataItems.some((e) => { return e.id === id; });
				};
				const dataItemJSON = {
					aggregate: mappedDataItem.getAggregation(),
					itemId: mappedDataItem.getColumnId(),
					id: mappedDataItem.getId()
				};
				const dataItemMinJSON = {
					aggregate: 'min',
					itemId: mappedDataItem.getColumnId(),
					id: __SUMMARY_MIN_PREFIX__ + mappedDataItem.getId()
				};
				if (!hasDataItem(mappedDataItem.getId()) && !hasDataItem(__SUMMARY_MIN_PREFIX__ + mappedDataItem.getId())) {
					queryDef.dataItems.push(dataItemJSON);
					queryDef.projections.push(mappedDataItem.getId());
					queryDef.dataItems.push(dataItemMinJSON);
					queryDef.projections.push(dataItemMinJSON.id);
				}
			}
		}
	}

	/**
	 * Process the value of an item returned from the query by adding it to the summaryCollector
	 * @param {String} dataItemUniqueId - the id of the dataItem.
	 * @param {String} aggregate - the aggregation type (eg countdistinct)
	 * @param {String} overallSummaryUseValue - the actual value (to be added as either a total or a min (for finding negatives).
	 * @param {Object} summaryCollector - the summaryCollector to add the value to.
	 */
	processValue(dataItemUniqueId, aggregate, overallSummaryUseValue, summaryCollector) {
		if (!this._isSummaryMinResult(dataItemUniqueId)) {
			this.overallSummariesCache[dataItemUniqueId]={
				aggregationType: aggregate,
				value: overallSummaryUseValue
			};
			this.updateSummaryEntry(summaryCollector, dataItemUniqueId, overallSummaryUseValue, 'summaryValueUse', 'summaryValue');
		} else {
			this._updateHasNegativeValuesFromSummaryMin(summaryCollector, dataItemUniqueId, overallSummaryUseValue);
		}
	}

	_isSummaryMinResult(dataItemUniqueId) {
		//A summary min is prefixed with __min__
		return (dataItemUniqueId.indexOf(__SUMMARY_MIN_PREFIX__) === 0) ? true : false;
	}

	//If this item is a 'summary min' (uniqueId starts with __min__), update the summary min.  If not, return false.
	_updateHasNegativeValuesFromSummaryMin(summaryCollector, dataItemUniqueId, overallSummaryMin) {
		if (this._isSummaryMinResult(dataItemUniqueId)) {
			const baseUniqueId = dataItemUniqueId.substr(__SUMMARY_MIN_PREFIX__.length);
			let summaryEntry = summaryCollector.getSummary(baseUniqueId);
			if (summaryEntry && overallSummaryMin < 0) {
				summaryEntry.hasNegativeValues = true;
				this.overallSummariesCache[baseUniqueId].hasNegativeValues = true;
			}
			return true;
		}
		return false;
	}
}

export default OverallSummaryValueQuery;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/OverallSummaryValueQuery.js